import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAtomValue } from "jotai";
import { userAtom } from "@/stores/authStore";
import { useUpdateCustomPolicy, useUpdateCustomPolicyProcedure } from "@/api/customPolicy.api";
import { GetCustomPolicyDto, GetCustomResponse } from "@/types/api/api-types";
import {
  CustomPolicyFormSchema,
  CustomPolicyFormData,
  mapPolicyToFormData,
} from "./custom-policy.form";
import { MemoizedCustomPolicyHeader } from "./customPolicyHeader";
import { MemoizedCustomPolicyDetailsCard } from "./customPolicyDetailsCard";
import { CustomPolicyRestrictionsSection } from "./customPolicyRestrictionsSection";
import { CustomPolicyDocumentsSection } from "./customPolicyDocumentsSection";
import { CustomPolicyProcedureSection } from "./customPolicyProcedureSection";
import { UnsavedChangesModal } from "./unsavedChanges.modal";

type Props = {
  custom: GetCustomResponse;
  policy: GetCustomPolicyDto;
  customsId: string;
};

export const CustomPolicyForm = ({ custom, policy, customsId }: Props) => {
  const navigate = useNavigate();
  const user = useAtomValue(userAtom);
  const isSuperAdmin = user?.role === "SUPERADMIN";

  const { mutateAsync: updatePolicy, isPending: isUpdatingPolicy } = useUpdateCustomPolicy();
  const { mutateAsync: updateProcedure, isPending: isUpdatingProcedure } =
    useUpdateCustomPolicyProcedure();

  const isPending = isUpdatingPolicy || isUpdatingProcedure;

  const {
    control,
    handleSubmit,
    watch,
    formState: { isDirty },
  } = useForm<CustomPolicyFormData>({
    defaultValues: mapPolicyToFormData(policy),
    resolver: zodResolver(CustomPolicyFormSchema),
    mode: "onSubmit",
  });

  const [showCloseDialog, setShowCloseDialog] = useState(false);

  const onSubmit: SubmitHandler<CustomPolicyFormData> = async (data) => {
    if (isSuperAdmin) {
      await updatePolicy({
        customsId,
        payload: {
          ...data,
          notes: data.notes ?? undefined,
          procedure: data.procedure ?? undefined,
        },
      });
    } else {
      await updateProcedure({
        customsId,
        payload: { procedure: data.procedure ?? undefined },
      });
    }
    navigate(-1);
  };

  const handleClose = () => {
    if (isDirty) {
      setShowCloseDialog(true);
    } else {
      navigate(-1);
    }
  };

  const maxPurchase = watch("maxPurchase");

  return (
    <div className="absolute inset-0 flex flex-col overflow-hidden">
      <MemoizedCustomPolicyHeader
        onSave={handleSubmit(onSubmit)}
        onClose={handleClose}
        isPending={isPending}
      />

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="flex justify-center gap-8 p-8">
          <MemoizedCustomPolicyDetailsCard custom={custom} policy={policy} />

          <form className="flex max-w-3xl flex-1 flex-col gap-8" onSubmit={handleSubmit(onSubmit)}>
            <CustomPolicyRestrictionsSection
              control={control}
              isSuperAdmin={isSuperAdmin}
              maxPurchase={maxPurchase}
            />
            <CustomPolicyDocumentsSection control={control} isSuperAdmin={isSuperAdmin} />
            <CustomPolicyProcedureSection control={control} />
          </form>
        </div>
      </div>

      <UnsavedChangesModal
        open={showCloseDialog}
        onClose={() => setShowCloseDialog(false)}
        onConfirm={() => navigate(-1)}
      />
    </div>
  );
};
